import { useState, useMemo } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  useActivatePricingVersion,
  useDeletePricingVersion,
} from '@/hooks/useAdmin'
import { useQuery } from '@tanstack/react-query'
import { adminQueries } from '@/services/adminApi'
import { AdminPricingVersion, AdminPricingRate } from '@/services/adminApi'
import { COUNTRIES_CONFIG } from '@/config/countries'
import { getCountryName } from '@/lib/countries'
import { Flag } from '@/components/Flag'
import { ArrowLeft, Trash2, CheckCircle, ChevronDown, ChevronRight, ChevronLeftIcon, ChevronRightIcon, History, ChevronsUpDown, ChevronUp } from 'lucide-react'

function SimplePagination({
  page,
  totalPages,
  totalItems,
  onPageChange,
}: {
  page: number
  totalPages: number
  totalItems: number
  onPageChange: (page: number) => void
}) {
  if (totalPages <= 1) return null

  return (
    <div className="flex items-center justify-between pt-4">
      <span className="text-sm text-muted-foreground">
        {totalItems} total
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
        >
          <ChevronLeftIcon className="h-4 w-4" />
          Previous
        </Button>
        <span className="text-sm text-muted-foreground">
          Page {page} of {totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
        >
          Next
          <ChevronRightIcon className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

type RateSortKey = 'country' | 'type' | 'provider' | 'cost' | 'price' | 'margin'
type SortDir = 'asc' | 'desc'

function SortIcon({ column, sortKey, sortDir }: { column: RateSortKey; sortKey: RateSortKey; sortDir: SortDir }) {
  if (column !== sortKey) return <ChevronsUpDown className="ml-1 inline h-3 w-3 opacity-40" />
  return sortDir === 'asc'
    ? <ChevronUp className="ml-1 inline h-3 w-3" />
    : <ChevronDown className="ml-1 inline h-3 w-3" />
}

function sortRates(rates: AdminPricingRate[], key: RateSortKey, dir: SortDir): AdminPricingRate[] {
  const mul = dir === 'asc' ? 1 : -1
  return [...rates].sort((a, b) => {
    let cmp = 0
    if (key === 'country') {
      const nameA = getCountryName(a.isoCountry) ?? a.isoCountry
      const nameB = getCountryName(b.isoCountry) ?? b.isoCountry
      cmp = nameA.localeCompare(nameB)
    } else if (key === 'type') {
      cmp = a.inferredType.localeCompare(b.inferredType)
    } else if (key === 'provider') {
      cmp = (a.provider ?? '').localeCompare(b.provider ?? '')
    } else if (key === 'cost') {
      cmp = a.costPerMin - b.costPerMin
    } else if (key === 'price') {
      cmp = a.pricePerMin - b.pricePerMin
    } else if (key === 'margin') {
      const mA = a.pricePerMin > 0 ? (a.pricePerMin - a.costPerMin) / a.pricePerMin : 0
      const mB = b.pricePerMin > 0 ? (b.pricePerMin - b.costPerMin) / b.pricePerMin : 0
      cmp = mA - mB
    }
    return cmp * mul
  })
}

function VersionRatesTable({ versionId }: { versionId: string }) {
  const { data: rates, isLoading } = useQuery(adminQueries.pricingVersionRates(versionId))
  const [countryFilter, setCountryFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [sortKey, setSortKey] = useState<RateSortKey>('country')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  function handleSort(key: RateSortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const filtered = useMemo(() => {
    if (!rates) return []
    const f = rates.filter((r) => {
      if (countryFilter) {
        const searchable = `${r.isoCountry} ${getCountryName(r.isoCountry)}`.toLowerCase()
        if (!searchable.includes(countryFilter.toLowerCase())) return false
      }
      if (typeFilter && r.inferredType !== typeFilter) return false
      return true
    })
    return sortRates(f, sortKey, sortDir)
  }, [rates, countryFilter, typeFilter, sortKey, sortDir])

  const types = useMemo(() => {
    if (!rates) return []
    return [...new Set(rates.map((r: AdminPricingRate) => r.inferredType))].sort()
  }, [rates])

  const sortProps = (key: RateSortKey, extraClass?: string) => ({
    className: ['cursor-pointer select-none hover:text-foreground', extraClass].filter(Boolean).join(' '),
    onClick: () => handleSort(key),
  })

  if (isLoading) return <Skeleton className="h-32 w-full" />
  if (!rates?.length) return <p className="text-sm text-muted-foreground">No rates in this version.</p>

  return (
    <div className="space-y-3">
      <div className="flex gap-3">
        <div className="max-w-48">
          <Input
            placeholder="Filter country..."
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            className="h-8 text-sm"
          />
        </div>
        <div className="flex gap-1.5">
          <Button
            size="sm"
            variant={typeFilter === '' ? 'default' : 'outline'}
            onClick={() => setTypeFilter('')}
            className="h-8 text-xs"
          >
            All
          </Button>
          {types.map((t) => (
            <Button
              key={t}
              size="sm"
              variant={typeFilter === t ? 'default' : 'outline'}
              onClick={() => setTypeFilter(t)}
              className="h-8 text-xs"
            >
              {t}
            </Button>
          ))}
        </div>
        <span className="ml-auto self-center text-xs text-muted-foreground">
          {filtered.length} of {rates.length} routes
        </span>
      </div>
      <div className="max-h-96 overflow-y-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead {...sortProps('country')}>
                Country<SortIcon column="country" sortKey={sortKey} sortDir={sortDir} />
              </TableHead>
              <TableHead {...sortProps('type')}>
                Type<SortIcon column="type" sortKey={sortKey} sortDir={sortDir} />
              </TableHead>
              <TableHead {...sortProps('provider')}>
                Provider<SortIcon column="provider" sortKey={sortKey} sortDir={sortDir} />
              </TableHead>
              <TableHead>Prefixes</TableHead>
              <TableHead>Origin</TableHead>
              <TableHead {...sortProps('cost', 'text-right')}>
                Cost<SortIcon column="cost" sortKey={sortKey} sortDir={sortDir} />
              </TableHead>
              <TableHead {...sortProps('price', 'text-right')}>
                Our Price<SortIcon column="price" sortKey={sortKey} sortDir={sortDir} />
              </TableHead>
              <TableHead {...sortProps('margin', 'text-right')}>
                Margin<SortIcon column="margin" sortKey={sortKey} sortDir={sortDir} />
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((rate, i) => {
              const country = COUNTRIES_CONFIG[rate.isoCountry]
              const margin = rate.pricePerMin > 0 && rate.costPerMin > 0
                ? ((rate.pricePerMin - rate.costPerMin) / rate.pricePerMin * 100).toFixed(0)
                : null
              return (
                <TableRow key={i}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {country && <Flag countryCode={country.code} size="sm" />}
                      {getCountryName(rate.isoCountry)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        rate.inferredType === 'mobile' ? 'default'
                          : rate.inferredType === 'landline' ? 'secondary'
                          : 'outline'
                      }
                      className="text-xs"
                    >
                      {rate.inferredType}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={rate.provider === 'telnyx' ? 'outline' : 'secondary'}
                      className="text-xs capitalize"
                    >
                      {rate.provider ?? 'twilio'}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs max-w-48 truncate" title={rate.destPrefixes.join(', ')}>
                    {rate.destPrefixes.slice(0, 5).join(', ')}
                    {rate.destPrefixes.length > 5 && ` +${rate.destPrefixes.length - 5}`}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {rate.origPrefixes.slice(0, 5).join(', ')}
                    {rate.origPrefixes.length > 5 && ` +${rate.origPrefixes.length - 5}`}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {rate.costPerMin}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs font-medium">
                    {rate.pricePerMin}
                  </TableCell>
                  <TableCell className="text-right text-xs">
                    {margin ? `${margin}%` : '-'}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function VersionRow({
  version,
  isExpanded,
  onToggle,
  onActivate,
  onDelete,
  activating,
  deleting,
}: {
  version: AdminPricingVersion
  isExpanded: boolean
  onToggle: () => void
  onActivate: () => void
  onDelete: () => void
  activating: boolean
  deleting: boolean
}) {
  return (
    <div
      className={`rounded-lg border ${version.isActive ? 'border-green-500/50 bg-green-500/5' : ''}`}
    >
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onToggle}>
            {isExpanded
              ? <ChevronDown className="h-4 w-4" />
              : <ChevronRight className="h-4 w-4" />
            }
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm">{version.id.slice(0, 8)}</span>
              {version.isActive && (
                <Badge variant="default" className="bg-green-600">Active</Badge>
              )}
            </div>
            <div className="flex gap-4 text-sm text-muted-foreground">
              <span>
                Generated: {version.generatedAt
                  ? new Date(version.generatedAt * 1000).toLocaleString()
                  : 'N/A'}
              </span>
              <span>{version.totalRoutes} routes</span>
              <span>{version.totalPrefixes} prefixes</span>
              <span>{version.currency}</span>
            </div>
          </div>
        </div>
        {!version.isActive && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={onActivate}
              disabled={activating}
            >
              <CheckCircle className="mr-1 h-4 w-4" />
              Activate
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-destructive"
              onClick={onDelete}
              disabled={deleting}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      {isExpanded && (
        <div className="border-t px-3 py-3">
          <VersionRatesTable versionId={version.id} />
        </div>
      )}
    </div>
  )
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString()
}

function formatDuration(activatedAt: string, deactivatedAt: string | null) {
  const start = new Date(activatedAt).getTime()
  const end = deactivatedAt ? new Date(deactivatedAt).getTime() : Date.now()
  const diffMs = end - start
  const minutes = Math.floor(diffMs / 60000)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (days > 0) return `${days}d ${hours % 24}h`
  if (hours > 0) return `${hours}h ${minutes % 60}m`
  return `${minutes}m`
}

export const Route = createFileRoute('/admin/pricing/versions')({
  component: AdminPricingVersionsPage,
})

function AdminPricingVersionsPage() {
  const [versionsPage, setVersionsPage] = useState(1)
  const [activationsPage, setActivationsPage] = useState(1)
  const pageSize = 20

  const { data: versionsData, isLoading: versionsLoading } = useQuery(adminQueries.pricingVersions(versionsPage, pageSize))
  const { data: activationsData, isLoading: activationsLoading } = useQuery(adminQueries.pricingActivations(activationsPage, pageSize))
  const activateMutation = useActivatePricingVersion()
  const deleteMutation = useDeletePricingVersion()

  const [expandedVersionId, setExpandedVersionId] = useState<string | null>(null)

  const versions = versionsData?.versions ?? []
  const activations = activationsData?.activations ?? []

  const handleActivate = (id: string) => {
    if (!confirm('Are you sure you want to activate this pricing version? This will replace the current active pricing.')) return
    activateMutation.mutate(id)
  }

  const handleDelete = (id: string) => {
    if (!confirm('Are you sure you want to delete this pricing version?')) return
    deleteMutation.mutate(id)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/admin/pricing">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to Pricing
          </Link>
        </Button>
        <h1 className="text-3xl font-bold">Pricing Versions</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Versions</CardTitle>
        </CardHeader>
        <CardContent>
          {versionsLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : !versions.length ? (
            <p className="text-sm text-muted-foreground">No pricing versions uploaded yet.</p>
          ) : (
            <div className="space-y-2">
              {versions.map((version) => (
                <VersionRow
                  key={version.id}
                  version={version}
                  isExpanded={expandedVersionId === version.id}
                  onToggle={() => setExpandedVersionId(
                    expandedVersionId === version.id ? null : version.id
                  )}
                  onActivate={() => handleActivate(version.id)}
                  onDelete={() => handleDelete(version.id)}
                  activating={activateMutation.isPending}
                  deleting={deleteMutation.isPending}
                />
              ))}
              <SimplePagination
                page={versionsPage}
                totalPages={versionsData?.totalPages ?? 1}
                totalItems={versionsData?.totalItems ?? 0}
                onPageChange={setVersionsPage}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <History className="h-5 w-5" />
            Activation History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activationsLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : !activations.length ? (
            <p className="text-sm text-muted-foreground">No activations yet.</p>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Version</TableHead>
                      <TableHead>Generated</TableHead>
                      <TableHead>Activated</TableHead>
                      <TableHead>Deactivated</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activations.map((activation) => (
                      <TableRow key={activation.id}>
                        <TableCell className="font-mono text-sm">
                          {activation.versionLabel}
                        </TableCell>
                        <TableCell className="text-sm">
                          {activation.generatedAt
                            ? new Date(activation.generatedAt * 1000).toLocaleString()
                            : 'N/A'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDateTime(activation.activatedAt)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {activation.deactivatedAt
                            ? formatDateTime(activation.deactivatedAt)
                            : '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDuration(activation.activatedAt, activation.deactivatedAt)}
                        </TableCell>
                        <TableCell>
                          {!activation.deactivatedAt ? (
                            <Badge variant="default" className="bg-green-600">Active</Badge>
                          ) : (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <SimplePagination
                page={activationsPage}
                totalPages={activationsData?.totalPages ?? 1}
                totalItems={activationsData?.totalItems ?? 0}
                onPageChange={setActivationsPage}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
